import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import SupervisorDashboard from '../pages/supervisor/dashboard';
import SupervisorReports from '../pages/supervisor/Reports';
import SupervisorSettings from '../pages/supervisor/Settings';
import SupervisorChat from '../pages/supervisor/Chat';

// Import organization member components to reuse
import AllMember from '../pages/organization/members/AllMember';
import CreateMember from '../pages/organization/members/CreateMember';
import ShowMember from '../pages/organization/members/ShowMember';
import CreateRoleForMember from '../pages/organization/members/CreateRoleForMember';

// Protected Route Component for Supervisor
const SupervisorProtectedRoute = ({ children, requiredPermission }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15213] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated and is a supervisor
  if (!user || user.accountType !== 'supervisor') {
    return <Navigate to="/auth/login" replace />;
  }

  // Check specific permission if required
  if (requiredPermission) {
    console.log("🔍 Checking permission:", requiredPermission);
    console.log("👤 User roles:", user?.metaData?.roles);
    console.log("🔧 Supervisor roles:", user?.metaData?.roles?.supervisor);
    
    let hasPermission = false;
    
    switch (requiredPermission) {
      case 'manage_members':
      case 'view_members':
        hasPermission = user?.metaData?.roles?.supervisor?.read === true || user?.metaData?.roles?.supervisor?.update === true;
        console.log("📋 Member permission check:", {
          read: user?.metaData?.roles?.supervisor?.read,
          update: user?.metaData?.roles?.supervisor?.update,
          hasPermission
        });
        break;
      case 'view_reports':
        hasPermission = user?.metaData?.roles?.reports?.read === true || user?.metaData?.roles?.reports?.export === true;
        console.log("📊 Reports permission check:", {
          read: user?.metaData?.roles?.reports?.read,
          export: user?.metaData?.roles?.reports?.export,
          hasPermission
        });
        break;
      case 'manage_settings':
        hasPermission = user?.metaData?.roles?.supervisor?.update === true;
        console.log("⚙️ Settings permission check:", {
          update: user?.metaData?.roles?.supervisor?.update,
          hasPermission
        });
        break;
      default:
        hasPermission = false;
        console.log("❌ Unknown permission:", requiredPermission);
    }
    
    console.log("🎯 Final permission result:", hasPermission);
    
    if (!hasPermission) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access this page.</p>
            <div className="mt-4 text-xs text-gray-500">
              <p>Required: {requiredPermission}</p>
              <p>Debug: Check console for details</p>
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
};

// Main Supervisor Routes Component
const SupervisorRoutes = () => {
  const { user } = useAuth();

  // Check if user has member management permissions based on roles
  const hasMemberPermissions = user?.metaData?.roles?.supervisor?.read === true || user?.metaData?.roles?.supervisor?.update === true;
  
  console.log("🚦 Route permissions check:", {
    user: user?.firstName + " " + user?.lastName,
    accountType: user?.accountType,
    supervisorRoles: user?.metaData?.roles?.supervisor,
    hasMemberPermissions,
    reportsPermissions: {
      read: user?.metaData?.roles?.reports?.read,
      export: user?.metaData?.roles?.reports?.export
    }
  });

  return (
    <Routes>
        {/* Dashboard - Always accessible to supervisors */}
        <Route 
          path="/dashboard" 
          element={
            <SupervisorProtectedRoute>
              <SupervisorDashboard />
            </SupervisorProtectedRoute>
          } 
        />

        {/* Members - Using organization components */}
        {hasMemberPermissions && (
          <>
            <Route 
              path="/members" 
              element={
                <SupervisorProtectedRoute requiredPermission="view_members">
                  <AllMember />
                </SupervisorProtectedRoute>
              } 
            />
            <Route 
              path="/members/create" 
              element={
                <SupervisorProtectedRoute requiredPermission="view_members">
                  <CreateMember />
                </SupervisorProtectedRoute>
              } 
            />
            <Route 
              path="/members/:memberId" 
              element={
                <SupervisorProtectedRoute requiredPermission="view_members">
                  <ShowMember />
                </SupervisorProtectedRoute>
              } 
            />
            <Route 
              path="/members/create-role/:memberId" 
              element={
                <SupervisorProtectedRoute requiredPermission="view_members">
                  <CreateRoleForMember />
                </SupervisorProtectedRoute>
              } 
            />
          </>
        )}

        {/* Reports - Only if user has report permissions */}
        {(user?.metaData?.roles?.reports?.read === true || user?.metaData?.roles?.reports?.export === true) && (
          <Route 
            path="/reports" 
            element={
              <SupervisorProtectedRoute requiredPermission="view_reports">
                <SupervisorReports />
              </SupervisorProtectedRoute>
            } 
          />
        )}

        {/* Chat - Always accessible to supervisors */}
        <Route 
          path="/chat" 
          element={
            <SupervisorProtectedRoute>
              <SupervisorChat />
            </SupervisorProtectedRoute>
          } 
        />

        {/* Settings - Only if user has settings permissions */}
        {user?.metaData?.roles?.supervisor?.update === true && (
          <Route 
            path="/settings" 
            element={
              <SupervisorProtectedRoute requiredPermission="manage_settings">
                <SupervisorSettings />
              </SupervisorProtectedRoute>
            } 
          />
        )}

        {/* Default redirect to dashboard */}
        <Route path="/" element={<Navigate to="/supervisor/dashboard" replace />} />
        
        {/* 404 for supervisor routes */}
        <Route 
          path="*" 
          element={
            <SupervisorProtectedRoute>
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
                  <p className="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
                  <button
                    onClick={() => window.history.back()}
                    className="px-4 py-2 bg-[#F15213] text-white rounded-lg hover:bg-[#d63f0f] transition-colors"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </SupervisorProtectedRoute>
          } 
        />
      </Routes>
  );
};

// Export routes array for integration with main router
export const supervisorRoutes = [
  { path: "/supervisor/*", element: <SupervisorRoutes /> }
];

export default SupervisorRoutes; 